import { Button, Card, DownloadIcon, Table, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import { memo, useState, type FC } from "react";
import { createPortal } from "react-dom";
import Icon from "../../../components/ui/Icon";
import useDropdownMenu from "../../../hooks/useDropdownMenu";
import useI18n from "../../../hooks/useTranslations";
import { getUILanguages } from "../../../types";
import type { BillRow, ExportAction, PdfExportAction } from "./billOfQuantities.types";
import styles from "./BillOfQuantitiesCard.module.css";

type BillOfQuantitiesCardProps = {
  rows: BillRow[];
  exportActions: ExportAction[];
  pdfExport: PdfExportAction;
  // Renders just the title and content, as a section of a parent Card, instead of its own Card.
  embedded?: boolean;
};

const BillOfQuantitiesCard: FC<BillOfQuantitiesCardProps> = ({ rows, pdfExport, embedded = false }) => {
  const i18n = useI18n();
  const common = i18n.translation.common;
  const calculation = i18n.translation.deckDesignerModule.configurations.calculation;
  const languages = getUILanguages(i18n.translation);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = useDropdownMenu(isMenuOpen, () => setIsMenuOpen(false));

  const content = (
    <>
      {rows.length > 0 ? (
        <Table dense>
          <thead>
            <TableRow>
              <TableHeadCell align="left">{common.code}</TableHeadCell>
              <TableHeadCell align="left">{calculation.billOfQuantitiesProduct}</TableHeadCell>
              <TableHeadCell align="left">{common.description}</TableHeadCell>
              <TableHeadCell align="left">{common.category}</TableHeadCell>
              <TableHeadCell align="right">{calculation.billOfQuantitiesRequiredQuantity}</TableHeadCell>
              <TableHeadCell align="right">{calculation.billOfQuantitiesQuantityToOrder}</TableHeadCell>
            </TableRow>
          </thead>
          <tbody>
            {rows.map((row) => (
              <TableRow key={row.kind}>
                <TableCell className={styles.code} noWrap>
                  {row.code}
                </TableCell>
                <TableCell>{row.productName}</TableCell>
                <TableCell className={styles.description}>{row.description}</TableCell>
                <TableCell className={styles.category}>{row.label}</TableCell>
                <TableCell className={styles.quantity}>{row.requiredQuantity}</TableCell>
                <TableCell className={styles.quantity}>{row.quantity}</TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className={styles.empty}>{calculation.billOfQuantitiesEmpty}</div>
      )}

      <div className={styles.exportActions}>
        <p className={styles.exportDescription}>{calculation.downloadPdfDescription}</p>
        {/* Context menu listing all 5 languages - pick one to download the PDF in that language. */}
        <div ref={triggerRef} className={styles.menuWrapper}>
          <Button
            variant="primary"
            className={styles.exportAction}
            disabled={pdfExport.disabled}
            isLoading={pdfExport.isLoading}
            onClick={() => setIsMenuOpen((open) => !open)}
            title={pdfExport.label}
          >
            <Icon glyph={DownloadIcon} />
            {pdfExport.label}
          </Button>
          {pdfExport.disabledHint && <span className={styles.pdfDisabledTooltip}>{pdfExport.disabledHint}</span>}
          {/* Portaled, as the design-system ContextMenu: the scrolling results panel can't clip it. */}
          {isMenuOpen &&
            createPortal(
              <ul ref={menuRef} className={styles.languageMenu} style={menuStyle}>
                {languages.map((language) => (
                  <li
                    key={language.value}
                    onClick={() => {
                      setIsMenuOpen(false);
                      pdfExport.download(language);
                    }}
                  >
                    {language.display}
                  </li>
                ))}
              </ul>,
              document.body,
            )}
        </div>

        {/* DXF export buttons hidden for now: uncomment (and add exportActions back to the props
            destructuring above) to bring them back.
        {exportActions.map((action) => (
          <Button
            key={action.label}
            variant="primary"
            aria-label={action.label}
            className={styles.exportAction}
            disabled={action.disabled}
            onClick={action.onClick}
            title={action.label}
          >
            <Icon glyph={DownloadIcon} />
            {action.label}
          </Button>
        ))}
        */}
      </div>
    </>
  );

  // Embedded in the results Card's tabs: the tab title already names it.
  return embedded ? (
    content
  ) : (
    <Card
      style={{ minHeight: 400 }}
      accentColor="var(--accent-base, var(--red))"
      shadow
      title={calculation.billOfQuantities}
    >
      {content}
    </Card>
  );
};

// Memoized: DeckDesignerResults re-renders on every result change, but this component's props are
// stable - it re-renders through its own watched fields and props only.
export default memo(BillOfQuantitiesCard);
